<script>
    import SelectSearchableMixin from '@components/form/SelectSearchableMixin';
    import EmptyValueMixin from '@components/form/mixins/EmptyValueMixin';
    import LazySelectOptionsMixin from '@components/form/mixins/LazySelectOptionsMixin';
    // import runSelectSearchableTests from '@components/form/SelectSearchableTests'; // uncomment to run tests on mount

    export default {
        mixins: [EmptyValueMixin, SelectSearchableMixin, LazySelectOptionsMixin],
        model: {
            prop: 'value',
            event: 'change',
        },
        props: {
            property: {
                type: String,
                required: false,
                default: '',
            },
            allowEmpty: {
                type: Boolean,
                default: false,
            },
            value: {
                type: [String, Number],
                default: '',
            },
            disableChangeTrigger: {
                type: Boolean,
                default: false,
            },
            autoSelectSingleOption: {
                type: Boolean,
                default: false,
            },
            defaultValue: {
                type: [String, Number, null],
                default: null,
            },
        },
        emits: ['change', 'value-change'],
        data() {
            return {
                selectedOption: this.getSelectedOption(),
            };
        },
        computed: {
            customProps() {
                return {
                    'allow-empty': this.allowEmpty,
                    'data-value': this.value,
                    'value': this.selectedOption,
                    'disabled': this.dependencyDisabled,
                };
            },
            deselectLabel() {
                if (!this.allowEmpty) {
                    return '';
                }
                return this.$t('form.multiselect.deselect');
            },
        },
        watch: {
            internalOptions: {
                immediate: true,
                handler() {
                    const flatOptions = this.getFlatOptions(this.internalOptions);
                    const previousValue = this.selectedOption?.value ?? null;

                    // Only refresh selectedOption when the options list is non-empty. When the list is
                    // transiently empty (e.g. during a dependency reset before new options have loaded),
                    // loadSelectedOption would replace the existing display object with a synthetic
                    // {value, text: rawId} fallback that has no real label, causing a visible flicker
                    // where the raw ID is briefly rendered by vue-multiselect. Skip the refresh if
                    // options are empty and selectedOption already has a real display object — it will
                    // be refreshed once the real options arrive.
                    const skipRefresh = flatOptions.length === 0 && this.selectedOption !== null;
                    if (!skipRefresh) {
                        this.loadSelectedOption();
                    }

                    // Only emit a change event if the resolved selected option actually differs from
                    // what was selected before the options update. Emitting unconditionally causes
                    // spurious changeOption events with a stale option object on every options reload
                    // (e.g. when a dependent select reloads its list after a parent field changes).
                    const newValue = this.selectedOption?.value ?? null;
                    if (newValue !== previousValue) {
                        this.handleInput(this.selectedOption);
                    }

                    // Default-value auto-select
                    if (
                        !this.dependencyDisabled &&
                        this.defaultValue !== null &&
                        !this.selectedOption &&
                        flatOptions.some(o => o.value === this.defaultValue)
                    ) {
                        const defaultValue = flatOptions.find(o => o.value === this.defaultValue);
                        if (defaultValue) {
                            this.handleInput(defaultValue);
                            return;
                        }
                    }

                    // Single option auto-select
                    if (
                        !this.dependencyDisabled &&
                        this.autoSelectSingleOption &&
                        flatOptions.length === 1 &&
                        !this.selectedOption
                    ) {
                        const single = flatOptions[0];
                        this.handleInput(single);
                    }
                },
            },
            value(newValue, oldValue) {
                if (newValue === oldValue) {
                    return;
                }
                this.loadSelectedOption();
                this.$emit('value-change');
            },
        },
        mounted() {
            this.initializeSelected();

            if (
                !this.dependencyDisabled &&
                this.autoSelectSingleOption &&
                this.getFlatOptions(this.internalOptions).length === 1 &&
                !this.selectedOption
            ) {
                const single = this.getFlatOptions(this.internalOptions)[0];
                this.handleInput(single);
            }

            // Set to true to run regression tests on mount. Never commit as true.
            // if (true) runSelectSearchableTests(this.getFlatOptions.bind(this));
        },
        methods: {
            getSelectedOption() {
                const flat = this.getFlatOptions(this.baseOptions);
                const option = flat.find(opt => opt.value === this.value);
                if (option) {
                    return option;
                }

                if (this.value !== null && this.value !== undefined && this.value !== '') {
                    return {
                        value: this.value,
                        text: this.getLabelForOptionValue(this.value),
                    };
                }

                return null;
            },
            handleInput(selectedOption) {
                this.selectedOption = selectedOption;
                if (!this.disableChangeTrigger) {
                    this.$emit('change', selectedOption);
                }
            },
            initializeSelected() {
                this.loadSelectedOption();
            },
            isOptionSelected(option) {
                return option.value === this.value;
            },
            loadSelectedOption() {
                this.selectedOption = this.getSelectedOption();
            },
        },
    }
</script>
